import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Calendar, DollarSign, FileText, User, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface RFP {
  id: string;
  title: string;
  project_type: string;
  description: string;
  location: string;
  estimated_volume: number;
  timeline: string;
  status: string;
}

interface Proposal {
  id: string;
  developer_id: string;
  details: string;
  timeline: string;
  budget: number;
  documents: string[];
  status: string;
  created_at: string;
  developer_profile?: any;
}

interface ViewProposalsProps {
  isOpen: boolean;
  onClose: () => void;
  rfp: RFP;
}

export const ViewProposals: React.FC<ViewProposalsProps> = ({ isOpen, onClose, rfp }) => {
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && rfp.id) {
      fetchProposals();
    }
  }, [isOpen, rfp.id]);

  const fetchProposals = async () => {
    try {
      const { data, error } = await supabase
        .from('proposals')
        .select(`
          *,
          developer_profile:profiles!developer_id(display_name, company_name)
        `)
        .eq('rfp_id', rfp.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setProposals(data || []);
    } catch (error: any) {
      console.error('Error fetching proposals:', error);
      toast({
        title: "Error",
        description: "Failed to fetch proposals",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateProposalStatus = async (proposalId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('proposals')
        .update({ status })
        .eq('id', proposalId);

      if (error) throw error;

      toast({
        title: "Proposal Updated",
        description: `Proposal has been ${status}`,
      });

      fetchProposals(); // Refresh the list
    } catch (error: any) {
      console.error('Error updating proposal:', error);
      toast({
        title: "Error",
        description: "Failed to update proposal",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'accepted':
        return <Badge variant="default">Accepted</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Proposals for: {rfp.title}</DialogTitle>
          <DialogDescription>
            Review and manage proposals submitted by project developers
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : proposals.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Proposals Yet</h3>
              <p className="text-muted-foreground">
                When project developers submit proposals for your RFP, they will appear here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {proposals.map((proposal) => (
              <Card key={proposal.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <CardTitle className="text-lg">
                        {proposal.developer_profile?.display_name || 'Anonymous Developer'}
                      </CardTitle>
                      {proposal.developer_profile?.company_name && (
                        <CardDescription>
                          {proposal.developer_profile.company_name}
                        </CardDescription>
                      )}
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Submitted {new Date(proposal.created_at).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          Budget: ${proposal.budget?.toLocaleString() || 'Not specified'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(proposal.status)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">Proposal Details</h4>
                    <p className="text-muted-foreground">{proposal.details}</p>
                  </div>
                  
                  {proposal.timeline && (
                    <div>
                      <h4 className="font-medium mb-1">Proposed Timeline</h4>
                      <p className="text-sm text-muted-foreground">{proposal.timeline}</p>
                    </div>
                  )}

                  {proposal.documents && proposal.documents.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Supporting Documents</h4>
                      <div className="flex flex-wrap gap-2">
                        {proposal.documents.map((doc, index) => (
                          <Badge key={index} variant="outline">
                            <FileText className="w-3 h-3 mr-1" />
                            Document {index + 1}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {proposal.status === 'pending' && (
                    <>
                      <Separator />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => updateProposalStatus(proposal.id, 'accepted')}
                          className="flex items-center gap-1"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateProposalStatus(proposal.id, 'rejected')}
                          className="flex items-center gap-1"
                        >
                          <XCircle className="w-4 h-4" />
                          Decline
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};